import * as React from 'react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/client/components/ui/alert-dialog'
import { Button } from '~/client/components/ui/button'
import { AgreementCheckbox } from './AgreementCheckbox'
import { useCreateConsents } from '~/client/agreement/agreement.hooks'
import type { AgreementType, AgreementVersion } from '~/shared/agreement/agreement.supabase.schema'

type Props = {
  missingTypes: ReadonlyArray<AgreementType>
  activeVersions: ReadonlyArray<AgreementVersion>
}

export const ConsentRequiredModal: React.FC<Props> = ({ missingTypes, activeVersions }) => {
  const [checked, setChecked] = React.useState(false)
  const mutation = useCreateConsents()

  const missingVersionIds = React.useMemo(() => {
    const set = new Set<AgreementType>(missingTypes)
    return activeVersions.filter((v) => set.has(v.type)).map((v) => v.id)
  }, [missingTypes, activeVersions])

  const handleAccept = () => {
    if (!checked || mutation.isPending || missingVersionIds.length === 0) return
    mutation.mutate({
      agreement_version_ids: missingVersionIds,
      consent_url: typeof window === 'undefined' ? '/_app' : window.location.pathname,
    })
  }

  return (
    <AlertDialog open>
      <AlertDialogContent
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="sm:max-w-md"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Updated agreements</AlertDialogTitle>
          <AlertDialogDescription>
            We&apos;ve updated our agreements. Please review and accept the latest versions to keep using Revel Street.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <AgreementCheckbox
            id="consent-required-checkbox"
            types={missingTypes}
            checked={checked}
            onCheckedChange={setChecked}
            disabled={mutation.isPending}
            error={mutation.isError ? mutation.error?.message ?? 'Failed to record consent' : undefined}
          />
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            className="bg-[#8A3DFF] hover:bg-[#7B2FE6]"
            onClick={handleAccept}
            disabled={!checked || mutation.isPending || missingVersionIds.length === 0}
          >
            {mutation.isPending ? (
              <span className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Saving...
              </span>
            ) : (
              'Accept and Continue'
            )}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
